import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { formatPassCriteriaHint } from './training-pass-criteria-hint.js';

function routerNavigate(path) {
  const router = globalThis.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else globalThis.location.href = path;
}

function preferredTrainingOrigin() {
  if (globalThis.window === undefined) return '';
  return globalThis.location.origin;
}

Alpine.data('trainingListPage', () => ({
  mine: [],
  library: [],
  loading: true,
  busy: false,
  err: '',
  activeTab: 'training_session',
  aiFeaturesEnabled: false,
  /** Whether OPENAI_API_KEY is set on the server (ChatGPT API). */
  openAiConfigured: null,
  catalog: null,
  catalogLoading: false,
  selectedTierId: '',
  selectedLevelId: '',
  selectedSessionTemplateId: '',
  archiveDialogOpen: false,
  archiveTarget: null,
  /** 1–10 for new vs Computer sessions */
  ruleBotLevel: 5,
  /** Discover: text search + optional player filter */
  librarySearch: '',
  libraryPlayerId: '',
  libraryPlayerLabel: '',
  playerSuggest: [],
  playerSuggestLoading: false,
  _librarySearchTimer: null,
  _playerSuggestTimer: null,
  ghostLinkModalOpen: false,
  ghostLinkSession: null,
  /** After “Retry” / “Start over” — confirm, then create a new session to scorer */
  retryPreviewOpen: false,
  /** `completed` = last run stats; `in_progress` = starting fresh while a match is open */
  retryPreviewMode: null,
  retryPreviewTemplate: null,
  /** From GET /training/progress/me — template rows for Start / Retry labels */
  myProgress: null,

  ownerLabel(s) {
    const p = s.ownerPlayerId;
    if (!p) return 'Player';
    if (typeof p === 'object') return p.nickname || p.fullName || 'Player';
    return 'Player';
  },

  sessionStatusLabel(status) {
    const m = {
      draft: 'Draft',
      in_progress: 'In Progress',
      completed: 'Completed',
      archived: 'Archived',
    };
    return m[String(status || '')] || String(status || '').replaceAll('_', ' ');
  },

  sessionStatusPillClass(status) {
    const s = String(status || '');
    if (s === 'in_progress') {
      return 'bg-sky-500/25 text-sky-50 ring-1 ring-sky-400/45';
    }
    if (s === 'draft') return 'bg-neutral-600/45 text-neutral-200 ring-1 ring-neutral-500/35';
    if (s === 'completed') return 'bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-500/40';
    if (s === 'archived') return 'bg-neutral-700/55 text-neutral-400 ring-1 ring-neutral-600/35';
    return 'bg-neutral-600/35 text-neutral-300 ring-1 ring-neutral-500/25';
  },

  visibilityLabel(visibility) {
    const v = String(visibility || 'private');
    if (v === 'private') return 'Private';
    if (v === 'league') return 'League';
    if (v === 'link') return 'Link';
    return v;
  },

  visibilityPillClass(visibility) {
    const v = String(visibility || 'private');
    if (v === 'private') return 'bg-neutral-700/55 text-neutral-200 ring-1 ring-neutral-600/40';
    if (v === 'league') return 'bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-500/40';
    if (v === 'link') return 'bg-cyan-500/18 text-cyan-50 ring-1 ring-cyan-500/35';
    return 'bg-neutral-600/35 text-neutral-300 ring-1 ring-neutral-500/25';
  },

  /** Aligned with training scorer `isCatalogSolo` / server `isLivePracticeSoloScoring`. */
  sessionIsSoloLivePractice(s) {
    if (String(s?.sourceType) !== 'live_practice') return false;
    if (String(s?.catalogPracticeMode) === 'computer') return false;
    return String(s?.catalogPracticeMode) === 'solo' || String(s?.catalogPracticeMode) === '';
  },

  sourceTypeLabel(s) {
    const st = String(s?.sourceType || '');
    if (st === 'live_practice') {
      return this.sessionIsSoloLivePractice(s) ? 'Solo practice' : 'Practice (A / B)';
    }
    if (st === 'ghost_from_session') return 'Ghost';
    if (st === 'bot') return 'Computer';
    if (st === 'ai') return 'Sample leg';
    return st.replaceAll('_', ' ');
  },

  /** Robot for vs computer; ghost for ghost; dumbbell for solo / generic practice. */
  mySessionListLeadingIconClass(s) {
    const st = String(s?.sourceType || '');
    if (st === 'bot' || String(s?.catalogPracticeMode) === 'computer') {
      return 'fa-solid fa-robot text-amber-300/95';
    }
    if (st === 'ghost_from_session') {
      return 'fa-solid fa-ghost pdl-icon-ghost text-sky-300/90';
    }
    return 'fa-solid fa-dumbbell pdl-icon-training';
  },

  sessionFormatBlock(s) {
    const f = s?.format;
    return f && typeof f === 'object' ? f : {};
  },

  sessionStartingScoreText(s) {
    const f = this.sessionFormatBlock(s);
    const n = Number(f.startingScore);
    if (Number.isFinite(n) && n > 0) return String(n);
    const v = String(f.variant || '').trim();
    if (v) return v;
    return '';
  },

  /**
   * Small labeled chips: starting score, leg race, checkout, entry mode, AI/ghost extras.
   * (Computer vs human is the row leading icon; this row is the match format only.)
   */
  sessionFormatChips(s) {
    if (!s) return [];
    const f = this.sessionFormatBlock(s);
    const chips = [];
    const start = this.sessionStartingScoreText(s);
    if (start) {
      chips.push({
        key: 'start',
        icon: 'fa-solid fa-bullseye',
        iconClass: 'text-amber-300/85',
        text: start,
        title: 'Starting score to clear',
      });
    }
    const rawBo = Number(f.bestOfLegs);
    const bo = Number.isFinite(rawBo) && rawBo > 0 ? Math.min(31, Math.max(1, Math.floor(rawBo))) : 3;
    {
      const need = Math.floor(bo / 2) + 1;
      chips.push({
        key: 'legs',
        icon: 'fa-solid fa-trophy',
        iconClass: 'text-yellow-300/80',
        text: `Bo${bo}`,
        title: `Best of ${bo} legs — first to ${need}`,
      });
    }
    const chk = String(f.checkout || 'double-out');
    const outLabel = this.formatRuleHint(chk);
    if (outLabel) {
      let oIcon = 'fa-solid fa-bullseye';
      if (chk === 'master-out') oIcon = 'fa-solid fa-burst';
      if (chk === 'straight-out') oIcon = 'fa-solid fa-arrow-trend-down';
      chips.push({
        key: 'out',
        icon: oIcon,
        iconClass: 'text-sky-300/85',
        text: outLabel,
        title: 'Checkout rule',
      });
    }
    const dm = String(f.dartEntryMode || 'visit_total').toLowerCase();
    if (dm === 'per_dart_doubles') {
      chips.push({
        key: 'darts',
        icon: 'fa-solid fa-table',
        iconClass: 'text-violet-300/85',
        text: 'Doubles',
        title: 'Per-dart doubles (doubles drill)',
      });
    }
    if (String(s.sourceType) === 'bot' && String(s.botMeta?.kind) === 'openai') {
      chips.push({
        key: 'ai',
        icon: 'fa-solid fa-brain',
        iconClass: 'text-violet-200/90',
        text: 'API',
        title: 'ChatGPT API bot',
      });
    }
    if (this.sessionRuleBotLevel(s) != null) {
      const lvl = this.sessionRuleBotLevel(s);
      chips.push({
        key: 'level',
        icon: 'fa-solid fa-gauge',
        iconClass: 'text-amber-300/90',
        text: `L${lvl}`,
        title: 'Built-in bot level (1–10)',
      });
    }
    return chips;
  },

  sourceTypePillClass(s) {
    const st = String(s?.sourceType || '');
    if (st === 'live_practice') {
      return 'bg-emerald-500/18 text-emerald-100 ring-1 ring-emerald-500/40';
    }
    if (st === 'ghost_from_session') return 'bg-sky-500/18 text-sky-100 ring-1 ring-sky-500/40';
    if (st === 'bot') return 'bg-amber-500/20 text-amber-100 ring-1 ring-amber-500/45';
    if (st === 'ai') return 'bg-violet-500/18 text-violet-100 ring-1 ring-violet-500/40';
    return 'bg-neutral-600/35 text-neutral-300 ring-1 ring-neutral-500/25';
  },

  /** Icon class for the source-type pill (uses robot for built-in + ChatGPT; ghost; solo vs A/B). */
  sourceTypePillIcon(s) {
    const st = String(s?.sourceType || '');
    if (st === 'bot' && String(s?.botMeta?.kind) === 'openai') {
      return 'fa-solid fa-wand-magic-sparkles text-violet-200/90';
    }
    if (st === 'bot' || String(s?.catalogPracticeMode) === 'computer') {
      return 'fa-solid fa-robot text-amber-200/95';
    }
    if (st === 'ghost_from_session') return 'fa-solid fa-ghost text-sky-200/90';
    if (st === 'ai') return 'fa-solid fa-film text-violet-200/90';
    if (this.sessionIsSoloLivePractice(s)) {
      return 'fa-solid fa-user text-emerald-200/90';
    }
    return 'fa-solid fa-user-group text-emerald-200/80';
  },

  visibleMineSessions() {
    return Array.isArray(this.mine) ? this.mine : [];
  },

  tierVisual(tier) {
    const id = String(tier?.id || '').toLowerCase();
    let out;
    if (id.includes('advanced')) {
      out = {
        icon: 'fa-solid fa-crown',
        subtitle: 'Pressure and finishing precision',
        activeClass: 'border-violet-400/60 bg-violet-500/22 text-violet-100 shadow-md shadow-violet-900/25',
        idleClass: 'border-violet-500/25 bg-violet-950/20 text-violet-100/90 hover:bg-violet-900/35',
        iconWrapClass: 'bg-violet-500/20 text-violet-200',
      };
    } else if (id.includes('intermediate')) {
      out = {
        icon: 'fa-solid fa-bolt',
        subtitle: 'Consistency and scoring control',
        activeClass: 'border-sky-400/60 bg-sky-500/22 text-sky-100 shadow-md shadow-sky-900/25',
        idleClass: 'border-sky-500/25 bg-sky-950/20 text-sky-100/90 hover:bg-sky-900/35',
        iconWrapClass: 'bg-sky-500/20 text-sky-200',
      };
    } else {
      out = {
        icon: 'fa-solid fa-seedling',
        subtitle: 'Foundations and clean rhythm',
        activeClass: 'border-emerald-400/60 bg-emerald-500/22 text-emerald-100 shadow-md shadow-emerald-900/25',
        idleClass: 'border-emerald-500/25 bg-emerald-950/20 text-emerald-100/90 hover:bg-emerald-900/35',
        iconWrapClass: 'bg-emerald-500/20 text-emerald-200',
      };
    }
    const catIcon = typeof tier?.icon === 'string' ? tier.icon.trim() : '';
    if (catIcon) out = { ...out, icon: catIcon };
    const catTag = typeof tier?.tagline === 'string' ? tier.tagline.trim() : '';
    if (catTag) out = { ...out, subtitle: catTag };
    return out;
  },

  tiers() {
    return Array.isArray(this.catalog?.tiers) ? this.catalog.tiers : [];
  },

  currentTier() {
    return this.tiers().find((t) => String(t.id) === String(this.selectedTierId)) || null;
  },

  levels() {
    const tier = this.currentTier();
    return Array.isArray(tier?.levels) ? tier.levels : [];
  },

  currentLevel() {
    return this.levels().find((l) => String(l.id) === String(this.selectedLevelId)) || null;
  },

  templates() {
    const level = this.currentLevel();
    return Array.isArray(level?.sessions) ? level.sessions : [];
  },

  currentTemplate() {
    return this.templates().find((s) => String(s.id) === String(this.selectedSessionTemplateId)) || null;
  },

  setTab(tabId) {
    this.activeTab = String(tabId || 'training_session');
  },

  isTab(tabId) {
    return this.activeTab === tabId;
  },

  selectTier(tierId) {
    this.selectedTierId = String(tierId || '');
    this.onTierChange();
  },

  selectLevel(levelId) {
    this.selectedLevelId = String(levelId || '');
    this.onLevelChange();
  },

  selectTemplate(templateId) {
    this.selectedSessionTemplateId = String(templateId || '');
  },

  onTierChange() {
    const level = this.levels()[0] || null;
    this.selectedLevelId = level?.id || '';
    const session = this.templates()[0] || null;
    this.selectedSessionTemplateId = session?.id || '';
  },

  onLevelChange() {
    const session = this.templates()[0] || null;
    this.selectedSessionTemplateId = session?.id || '';
  },

  formatRuleHint(rule) {
    const r = String(rule || 'double-out');
    if (r === 'straight-out') return 'Straight Out';
    if (r === 'master-out') return 'Master Out';
    return 'Double Out';
  },

  templateFormatSummary(template) {
    const f = template?.format;
    if (!f) return '';
    const start = Number(f.startingScore);
    const label = Number.isFinite(start) ? String(start) : String(f.variant || '501');
    const chk = this.formatRuleHint(f.checkout);
    const bo = Number(f.bestOfLegs);
    const bon = Number.isFinite(bo) && bo > 0 ? bo : 3;
    return `${label}, ${chk}, Bo${bon}`;
  },

  /** One line: leg start score, checkout, leg race, darts per visit, solo vs computer + bot level. */
  templatePlaySummary(template) {
    if (!template || typeof template !== 'object') return '';
    const f = template.format || {};
    const startN = Number(f.startingScore);
    const start = Number.isFinite(startN) && startN > 0 ? String(startN) : String(f.variant || '501').trim() || '501';
    const chk = this.formatRuleHint(f.checkout);
    const rawBo = Number(f.bestOfLegs);
    const bo = Number.isFinite(rawBo) && rawBo > 0 ? Math.min(31, Math.max(1, Math.floor(rawBo))) : 3;
    const legsToWin = Math.floor(bo / 2) + 1;
    const dm = String(f.dartEntryMode || 'visit_total').trim().toLowerCase();
    const darts =
      dm === 'per_dart_doubles'
        ? 'Enter as doubles (1–3 scoring darts per visit)'
        : 'Up to 3 darts per visit';
    const pm = String(template.practiceMode || '').trim().toLowerCase();
    let opponent = 'Solo practice (no computer)';
    if (pm === 'computer') {
      const lvl = Number.isFinite(Number(template.computerRuleBotLevel))
        ? Math.min(10, Math.max(1, Math.round(Number(template.computerRuleBotLevel))))
        : 5;
      opponent = `vs Computer — built-in bot level ${lvl}/10`;
    }
    return `Leg starts at ${start} (score to clear) · ${chk} · Best of ${bo} legs (first to ${legsToWin}) · ${darts} · ${opponent}`;
  },

  criteriaHint(rule) {
    return formatPassCriteriaHint(rule);
  },

  templateCreatePayload() {
    const template = this.currentTemplate();
    const tier = this.currentTier();
    const level = this.currentLevel();
    if (!template || !tier || !level) return {};
    return this.templatePayloadFor(tier, level, template);
  },

  templatePayloadFor(tier, level, template) {
    if (!template || !tier || !level) return {};
    return {
      catalogVersion: String(this.catalog?.version || ''),
      tierId: tier.id,
      levelId: level.id,
      sessionTemplateId: template.id,
      title: String(template.label || 'Training session').slice(0, 200),
      format: template.format || undefined,
    };
  },

  templateProgressRow(tierId, levelId, sessionTemplateId) {
    const rows = Array.isArray(this.myProgress?.templateProgress) ? this.myProgress.templateProgress : [];
    return (
      rows.find(
        (r) =>
          String(r.tierId) === String(tierId) &&
          String(r.levelId) === String(levelId) &&
          String(r.sessionTemplateId) === String(sessionTemplateId),
      ) || null
    );
  },

  catalogTemplatePassed(row) {
    if (!row) return false;
    if (Number(row.completedCount) > 0) return true;
    return String(row.bestAwardTier || 'none') !== 'none';
  },

  trainingSessionIsLiveInProgress(s) {
    if (!s) return false;
    if (String(s.status) === 'completed' || String(s.status) === 'archived') return false;
    const ls = s.liveScoring;
    if (!ls) return false;
    if (String(ls.state) === 'finished') return false;
    const legs = ls.legs || [];
    if (!Array.isArray(legs) || legs.length === 0) return false;
    const bo = Math.max(1, Number(s.format?.bestOfLegs) || 9);
    const need = Math.ceil(bo / 2);
    const a = Number(ls.legsWonA) || 0;
    const b = Number(ls.legsWonB) || 0;
    if (a >= need || b >= need) return false;
    return true;
  },

  findInProgressSessionForTemplate(tier, level, template) {
    if (!tier || !level || !template?.id) return null;
    const tid = String(tier.id);
    const lid = String(level.id);
    const tpid = String(template.id);
    for (const s of this.visibleMineSessions()) {
      if (String(s.tierId) !== tid) continue;
      if (String(s.levelId) !== lid) continue;
      if (String(s.sessionTemplateId) !== tpid) continue;
      if (this.trainingSessionIsLiveInProgress(s)) return s;
    }
    return null;
  },

  lastRunSummaryLines(row) {
    if (!row) return [];
    const m = row.lastMetrics || {};
    const lines = [];
    if (row.lastAwardTier && String(row.lastAwardTier) !== 'none') {
      lines.push({ k: 'Last badge', v: String(row.lastAwardTier) });
    }
    if (Number(m.legsPlayed) > 0) lines.push({ k: 'Legs played', v: m.legsPlayed });
    if (Number(m.legsWonA) > 0) lines.push({ k: 'Legs won (you)', v: m.legsWonA });
    if (Number(m.threeDartAverageA) > 0) lines.push({ k: '3-dart average', v: m.threeDartAverageA });
    if (Number(m.checkoutsA) > 0) lines.push({ k: 'Checkouts', v: m.checkoutsA });
    if (Number(m.tonPlusVisitsA) > 0) lines.push({ k: '100+ scores', v: m.tonPlusVisitsA });
    if (Number(m.bustsA) > 0) lines.push({ k: 'Busts', v: m.bustsA });
    if (Number(m.maximumsA) > 0) lines.push({ k: '180s', v: m.maximumsA });
    return lines;
  },

  retryPreviewLastRunLines() {
    const t = this.retryPreviewTemplate;
    if (!t?.id) return [];
    const tier = this.currentTier();
    const level = this.currentLevel();
    if (!tier || !level) return [];
    return this.lastRunSummaryLines(this.templateProgressRow(tier.id, level.id, t.id));
  },

  catalogTemplateActionLabel(template) {
    const tier = this.currentTier();
    const level = this.currentLevel();
    if (!tier || !level || !template) return 'Start';
    if (this.findInProgressSessionForTemplate(tier, level, template)) return 'Resume';
    const row = this.templateProgressRow(tier.id, level.id, template.id);
    if (this.catalogTemplatePassed(row)) return 'Retry';
    return 'Start';
  },

  /** Match venue kiosk training row buttons: Start violet, Resume amber, Retry sky. */
  catalogTemplateActionButtonClass(template) {
    const label = this.catalogTemplateActionLabel(template);
    if (label === 'Retry') return 'border-sky-500/40 bg-sky-600 hover:bg-sky-500';
    if (label === 'Resume') return 'border-amber-500/40 bg-amber-600 hover:bg-amber-500';
    return 'border-violet-500/40 bg-violet-600 hover:bg-violet-500';
  },

  closeRetryPreview() {
    this.retryPreviewOpen = false;
    this.retryPreviewMode = null;
    this.retryPreviewTemplate = null;
  },

  /** True when this template has a live, in-progress training session (Resume). */
  trainingTemplateHasInProgress(tier, level, template) {
    return !!this.findInProgressSessionForTemplate(tier, level, template);
  },

  openTrainingStartOverWizard(template) {
    const tier = this.currentTier();
    const level = this.currentLevel();
    if (!tier || !level || !template?.id) return;
    if (!this.findInProgressSessionForTemplate(tier, level, template)) return;
    this.retryPreviewMode = 'in_progress';
    this.retryPreviewTemplate = template;
    this.retryPreviewOpen = true;
  },

  async onTrainingCatalogTemplateAction(template) {
    const tier = this.currentTier();
    const level = this.currentLevel();
    if (!tier || !level || !template?.id) return;
    const inProg = this.findInProgressSessionForTemplate(tier, level, template);
    if (inProg?._id) {
      routerNavigate(`/admin/training/${inProg._id}/scorer`);
      return;
    }
    const row = this.templateProgressRow(tier.id, level.id, template.id);
    if (this.catalogTemplatePassed(row)) {
      this.retryPreviewMode = 'completed';
      this.retryPreviewTemplate = template;
      this.retryPreviewOpen = true;
      return;
    }
    await this.createLiveSessionFromWizardTemplate(template);
  },

  async confirmRetryStartNew() {
    const t = this.retryPreviewTemplate;
    this.closeRetryPreview();
    if (!t?.id) return;
    await this.createLiveSessionFromWizardTemplate(t);
  },

  async loadMyProgress() {
    try {
      const res = await apiFetch('/training/progress/me');
      const j = await res.json().catch(() => ({}));
      this.myProgress = res.ok ? j : null;
    } catch {
      this.myProgress = null;
    }
  },

  async loadCatalog() {
    this.catalogLoading = true;
    try {
      const res = await apiFetch('/training/catalog');
      const data = await res.json().catch(() => ({}));
      this.catalog = res.ok ? data?.catalog || null : null;
      const tier = this.tiers()[0] || null;
      this.selectedTierId = tier?.id || '';
      const level = this.levels()[0] || null;
      this.selectedLevelId = level?.id || '';
      const template = this.templates()[0] || null;
      this.selectedSessionTemplateId = template?.id || '';
    } catch {
      this.catalog = null;
    } finally {
      this.catalogLoading = false;
    }
  },

  /** Computer opponent level 1–10, or null (not shown for ChatGPT or non-bot). */
  sessionRuleBotLevel(s) {
    if (!s || String(s.sourceType) !== 'bot') return null;
    if (String(s.botMeta?.kind) === 'openai') return null;
    const n = Number(s.botMeta?.ruleBotLevel);
    if (!Number.isFinite(n)) return null;
    return Math.min(10, Math.max(1, Math.round(n)));
  },

  isAdmin() {
    return this.$store?.session?.hasRole?.('admin') === true;
  },

  ghostReplayCountLabel(s) {
    const n = Number(s?.ghostReplayCount);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `${n} ${n === 1 ? 'replay' : 'replays'} from others`;
  },

  trainingShareUrl(s) {
    if (!s?._id) return '';
    return `${preferredTrainingOrigin()}/admin/training?ghostSource=${encodeURIComponent(s._id)}`;
  },

  async copyTrainingShareLink(s) {
    const url = this.trainingShareUrl(s);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.$store?.toastr?.success?.('Link copied');
    } catch {
      this.$store?.toastr?.error?.('Could not copy link');
    }
  },

  libraryQueryString() {
    const qs = new URLSearchParams();
    const q = String(this.librarySearch || '').trim();
    if (q) qs.set('q', q.slice(0, 80));
    if (this.libraryPlayerId) qs.set('playerId', String(this.libraryPlayerId));
    const str = qs.toString();
    return str ? `?${str}` : '';
  },

  async init() {
    if (!this.$store.features.enabled('training')) {
      this.err = 'Training is disabled. Set FEATURE_TRAINING=1 on the server and VITE_FEATURE_TRAINING=true for the admin build.';
      this.loading = false;
      return;
    }
    await this.loadCatalog();
    await Promise.all([this.refresh(), this.loadMyProgress()]);
    await this.handleGhostSourceParam();
  },

  async refresh() {
    this.loading = true;
    this.err = '';
    try {
      const [mRes, lRes] = await Promise.all([
        apiFetch('/training/sessions/mine'),
        apiFetch(`/training/sessions/library${this.libraryQueryString()}`),
      ]);
      if (!mRes.ok) throw new Error((await mRes.json().catch(() => ({})))?.error || 'Failed to load sessions');
      if (!lRes.ok) throw new Error((await lRes.json().catch(() => ({})))?.error || 'Failed to load library');
      this.openAiConfigured = null;
      const mj = await mRes.json();
      const lj = await lRes.json();
      this.mine = mj.items || [];
      this.library = lj.items || [];
    } catch (e) {
      this.err = e?.message || String(e);
    } finally {
      this.loading = false;
    }
  },

  scheduleLibraryRefresh() {
    if (this._librarySearchTimer) clearTimeout(this._librarySearchTimer);
    this._librarySearchTimer = setTimeout(() => {
      this._librarySearchTimer = null;
      void this.refresh();
    }, 380);
  },

  async onLibrarySearchInput() {
    this.scheduleLibraryRefresh();
  },

  async fetchPlayerSuggest() {
    const q = String(this.libraryPlayerLabel || '').trim();
    if (q.length < 2) {
      this.playerSuggest = [];
      return;
    }
    this.playerSuggestLoading = true;
    try {
      const res = await apiFetch(`/training/players/search?q=${encodeURIComponent(q.slice(0, 80))}`);
      const j = await res.json().catch(() => ({}));
      this.playerSuggest = res.ok ? (j.items || []) : [];
    } catch {
      this.playerSuggest = [];
    } finally {
      this.playerSuggestLoading = false;
    }
  },

  onPlayerFilterInput() {
    if (this._playerSuggestTimer) clearTimeout(this._playerSuggestTimer);
    this._playerSuggestTimer = setTimeout(() => void this.fetchPlayerSuggest(), 280);
  },

  pickPlayerFilter(p) {
    if (!p?._id) return;
    this.libraryPlayerId = String(p._id);
    this.libraryPlayerLabel = p.nickname || p.fullName || '';
    this.playerSuggest = [];
    void this.refresh();
  },

  clearPlayerFilter() {
    this.libraryPlayerId = '';
    this.libraryPlayerLabel = '';
    this.playerSuggest = [];
    void this.refresh();
  },

  async setSessionVisibility(s, v) {
    if (!s?._id) return;
    if (!['league', 'link'].includes(v)) return;
    try {
      const res = await apiFetch(`/training/sessions/${s._id}`, {
        method: 'PATCH',
        body: { visibility: v },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Update failed');
      s.visibility = v;
      await this.refresh();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    }
  },

  async handleGhostSourceParam() {
    const id = new URLSearchParams(globalThis.location.search).get('ghostSource');
    if (!id) return;
    try {
      const res = await apiFetch(`/training/sessions/${id}`);
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.session) {
        this.$store?.toastr?.warning?.('That shared session is not available.');
        return;
      }
      const sess = j.session;
      if (!sess.allowGhostReplay || sess.visibility === 'private') {
        this.$store?.toastr?.warning?.('That session is not shared for ghost replay.');
        return;
      }
      this.ghostLinkSession = sess;
      this.ghostLinkModalOpen = true;
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || 'Could not open shared session');
    } finally {
      const url = new URL(globalThis.location.href);
      url.searchParams.delete('ghostSource');
      const next = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '');
      globalThis.history.replaceState({}, '', next);
    }
  },

  closeGhostLinkModal() {
    this.ghostLinkModalOpen = false;
    this.ghostLinkSession = null;
  },

  async confirmGhostFromLink() {
    const s = this.ghostLinkSession;
    if (!s?._id) {
      this.closeGhostLinkModal();
      return;
    }
    this.closeGhostLinkModal();
    await this.createGhostFromLibrary(s);
  },

  async adminRemoveSession(s) {
    if (!s?._id || !this.isAdmin()) return;
    if (!globalThis.confirm(`Remove training session “${s.title || 'Untitled'}” from the system?`)) return;
    this.busy = true;
    try {
      const res = await apiFetch(`/training/sessions/${s._id}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Delete failed');
      }
      await this.refresh();
      this.$store?.toastr?.success?.('Session removed');
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  openLaunch(s) {
    if (!s?._id) return;
    routerNavigate(`/admin/training/${s._id}/launch`);
  },

  openScorer(s) {
    if (!s?._id) return;
    routerNavigate(`/admin/training/${s._id}/scorer`);
  },

  async createLiveSessionFromWizardTemplate(template) {
    const tier = this.currentTier();
    const level = this.currentLevel();
    if (!tier || !level || !template?.id) return;
    this.busy = true;
    try {
      const body = {
        title: String(template.label || 'Training session').slice(0, 200),
        ...this.templatePayloadFor(tier, level, template),
      };
      const res = await apiFetch('/training/sessions', { method: 'POST', body });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      this.$store?.toastr?.success?.('Session ready');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async createSession() {
    this.busy = true;
    try {
      const res = await apiFetch('/training/sessions', {
        method: 'POST',
        body: { title: 'Practice session', ...this.templateCreatePayload() },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      this.$store?.toastr?.success?.('Session ready');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async createBotSession() {
    this.busy = true;
    try {
      const res = await apiFetch('/training/sessions', {
        method: 'POST',
        body: {
          title: 'vs Computer',
          ...this.templateCreatePayload(),
          sourceType: 'bot',
          botMeta: {
            kind: 'rule_based',
            label: 'Computer opponent',
            ruleBotLevel: Math.min(10, Math.max(1, Math.round(Number(this.ruleBotLevel) || 5))),
          },
        },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      this.$store?.toastr?.success?.('Session ready — vs Computer');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async createChatGptBotSession() {
    if (!this.aiFeaturesEnabled) return;
    if (this.openAiConfigured === false) {
      this.$store?.toastr?.error?.('Set OPENAI_API_KEY on the server to use the ChatGPT API bot.', 6000);
      return;
    }
    this.busy = true;
    try {
      const res = await apiFetch('/training/sessions', {
        method: 'POST',
        body: {
          title: 'vs ChatGPT API',
          ...this.templateCreatePayload(),
          sourceType: 'bot',
          botMeta: { kind: 'openai', label: 'ChatGPT API (OpenAI)' },
        },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      this.$store?.toastr?.success?.('Session ready — vs ChatGPT');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async createGhostFromLibrary(s) {
    if (!s?._id) return;
    this.busy = true;
    try {
      const res = await apiFetch('/training/sessions', {
        method: 'POST',
        body: {
          title: `Ghost: ${s.title || 'tape'}`,
          sourceType: 'ghost_from_session',
          ghostSourceId: s._id,
        },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      this.$store?.toastr?.success?.('Ghost session created — you are A');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async createAiDraft() {
    if (!this.aiFeaturesEnabled) return;
    if (this.openAiConfigured === false) {
      this.$store?.toastr?.error?.('Add OPENAI_API_KEY on the server, or use “Offline sample”.', 6000);
      return;
    }
    this.busy = true;
    try {
      const res = await apiFetch('/training/sessions/ai-draft', {
        method: 'POST',
        body: { title: 'OpenAI sample leg' },
        timeoutMs: 120_000,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || j?.message || 'Create failed');
      this.$store?.toastr?.success?.('OpenAI sample leg ready');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async createAiDraftOffline() {
    if (!this.aiFeaturesEnabled) return;
    this.busy = true;
    try {
      const res = await apiFetch('/training/sessions/ai-draft-synthetic', {
        method: 'POST',
        body: { title: 'Offline sample leg' },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      this.$store?.toastr?.success?.('Offline sample leg ready');
      await this.loadMyProgress();
      routerNavigate(`/admin/training/${j.session._id}/scorer`);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },

  async toggleShare(s) {
    const next = !s.allowGhostReplay;
    const visibility = next ? 'league' : 'private';
    try {
      const res = await apiFetch(`/training/sessions/${s._id}`, {
        method: 'PATCH',
        body: {
          allowGhostReplay: next,
          visibility,
        },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Update failed');
      s.allowGhostReplay = next;
      s.visibility = visibility;
      await this.refresh();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    }
  },

  openArchiveDialog(s) {
    this.archiveTarget = s;
    this.archiveDialogOpen = true;
  },

  closeArchiveDialog() {
    this.archiveDialogOpen = false;
    this.archiveTarget = null;
  },

  async confirmArchive() {
    const s = this.archiveTarget;
    if (!s?._id) {
      this.closeArchiveDialog();
      return;
    }
    this.busy = true;
    try {
      const res = await apiFetch(`/training/sessions/${s._id}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Delete failed');
      }
      this.closeArchiveDialog();
      await this.refresh();
      this.$store?.toastr?.success?.('Session archived');
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.busy = false;
    }
  },
}));
